/**
 * Publication Date Helper Controller
 *
 * Composes an EDTF publication date from plain fields, writes it into the hidden output field and keeps a
 * server-rendered preview (humanized text, validity) in step. Apply stays disabled until the preview says the
 * value is valid. A failed preview shows a notice and enables Apply only for a complete value the browser accepts;
 * the server validates it on submit.
 *
 * Composition rules:
 *   - the mode is the tab with aria-selected="true"
 *   - year pads to 4 digits, month and day to 2
 *   - a day without a month, or a month or day without a year, makes the value empty (nothing to preview)
 *   - a year over 4 digits or a month or day over 2 digits makes the value empty instead of being truncated
 *   - text a number field cannot read (e.g. "e", "+") makes the value empty and shows the rejected-input message
 *   - range: an empty side is open ('..'); both sides empty, or one side invalid, is empty
 *   - decade: first 3 digits of the padded year + 'X'; century: first 2 digits + 'XX'
 *   - exact: the checked qualifier radio (if the helper offers one) appends '~' or '?'; decade and range never do
 *   - presets pass the checked preset radio's value through; no-date yields the null token
 *
 * Events:
 *   - publication-date--helper:apply (cancelable, detail.value): preventDefault keeps Apply from submitting the form
 *
 * @extends ApplicationController
 */

import ApplicationController from '../../javascript/controllers/application_controller'

const DEBOUNCE_MS = 250

export default class extends ApplicationController {
  static targets = [
    'mode',
    'panel',
    'field',
    'era',
    'qualifier',
    'preset',
    'output',
    'apply',
    'frame',
    'notice',
    'rejected',
  ]
  static values = { previewUrl: String, nullToken: String }

  connect() {
    this.timer = null
    // The value the preview in the frame answers for, and whether Apply may go ahead with it.
    this.settled = { value: this.outputTarget.value, valid: this.previewIsValid() }
    this.syncApply()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  /**
   * Recomputes the value after any input or change inside the modal, and after a mode switch.
   */
  changed() {
    const rejected = this.activeFields().some((field) => field.validity.badInput)
    this.rejectedTarget.hidden = !rejected
    const value = rejected ? '' : this.compose()
    if (value === this.outputTarget.value) return

    this.outputTarget.value = value
    this.syncApply()
    this.noticeTarget.hidden = true

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.requestPreview(value), DEBOUNCE_MS)
  }

  /**
   * Announces the value; a listener may take it and cancel the default action (the form submission, when the
   * helper has a form).
   * @param {Event} event
   */
  apply(event) {
    const announced = this.dispatch('apply', {
      detail: { value: this.outputTarget.value },
      cancelable: true,
    })
    if (announced.defaultPrevented) event.preventDefault()
  }

  /**
   * Enables Apply when the preview that just loaded belongs to the current value and is valid.
   */
  previewLoaded() {
    const value = this.loadedValue()
    if (value !== this.outputTarget.value) return

    this.settled = { value, valid: this.previewIsValid() }
    this.noticeTarget.hidden = true
    this.syncApply()
  }

  /**
   * Lets Apply proceed when the preview cannot be fetched, but only for a complete value whose fields the browser
   * accepts; the server validates it on submit.
   * @param {Event} event
   */
  previewFailed(event) {
    event.preventDefault()
    const value = this.loadedValue()
    // Dropping src makes the next request for the same value fetch again
    this.frameTarget.removeAttribute('src')
    if (value !== this.outputTarget.value) return

    this.frameTarget.replaceChildren()
    this.settled = {
      value,
      valid: value !== '' && this.activeFields().every((f) => f.validity.valid),
    }
    this.noticeTarget.hidden = false
    this.syncApply()
  }

  /**
   * Degrades like a failed fetch when the response has no preview frame. An expired session (a redirect or a 401)
   * and a server error (5xx, reported by the server) are degraded paths, so they stop here. Any other response
   * without the frame (a 400, 403 or 404, or a 200) points at a markup or authorization bug and bubbles on to the
   * global turbo:frame-missing handler, which reports it.
   * @param {CustomEvent} event
   */
  previewMissing(event) {
    const { response } = event.detail
    if (response.redirected || response.status === 401 || response.status >= 500)
      event.stopPropagation()
    this.previewFailed(event)
  }

  /**
   * Selects the clicked mode tab.
   * @param {Event} event
   */
  select(event) {
    this.activate(event.currentTarget)
  }

  /**
   * Moves between mode tabs with the arrow keys (wrapping), Home and End; selection follows focus.
   * @param {KeyboardEvent} event
   */
  navigate(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

    const tabs = this.modeTargets
    const index = tabs.indexOf(event.currentTarget)
    const target = {
      ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
      ArrowRight: tabs[(index + 1) % tabs.length],
      Home: tabs[0],
      End: tabs[tabs.length - 1],
    }[event.key]
    if (!target) return

    event.preventDefault()
    this.activate(target)
  }

  /**
   * Marks the tab selected, shows its panel and recomputes the value for that mode.
   * @param {HTMLElement} tab
   */
  activate(tab) {
    this.modeTargets.forEach((other) => {
      const selected = other === tab
      other.setAttribute('aria-selected', String(selected))
      other.tabIndex = selected ? 0 : -1
    })
    tab.focus()

    const shown = this.shownPanel()
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel !== shown
    })
    this.changed()
  }

  requestPreview(value) {
    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set('value', value)

    if (this.frameTarget.src === url.href && !this.frameTarget.busy) {
      this.settled = { value, valid: this.previewIsValid() }
      this.syncApply()
      return
    }
    this.frameTarget.src = url.href
  }

  /** Apply is enabled only for the value the settled preview answered for, and only when that was valid. */
  syncApply() {
    const settled = this.settled
    this.applyTarget.disabled = !(
      settled &&
      settled.value === this.outputTarget.value &&
      settled.valid
    )
  }

  previewIsValid() {
    const marker = this.frameTarget.querySelector('[data-valid]')
    if (!marker) throw new Error('date helper preview has no [data-valid] marker')

    return marker.dataset.valid === 'true'
  }

  loadedValue() {
    const src = this.frameTarget.getAttribute('src')
    return src
      ? new URL(src, window.location.origin).searchParams.get('value')
      : this.outputTarget.value
  }

  currentMode() {
    const mode = this.modeTargets.find((tab) => tab.getAttribute('aria-selected') === 'true')
      ?.dataset.modeTab
    if (!['exact', 'range', 'decade', 'presets', 'none'].includes(mode)) {
      throw new Error(`date helper has unknown mode: ${mode}`)
    }

    return mode
  }

  compose() {
    switch (this.currentMode()) {
      case 'exact':
        return this.composeExact()
      case 'range':
        return this.composeRange()
      case 'decade':
        return this.composeDecade()
      case 'presets':
        return this.presetTargets.find((radio) => radio.checked)?.value ?? ''
      case 'none':
        return this.nullTokenValue
    }
  }

  composeExact() {
    const date = this.readDate('exact')
    if (date.state !== 'ok') return ''

    const mark = this.hasQualifierTarget
      ? (this.qualifierTargets.find((radio) => radio.checked)?.value ?? '')
      : ''
    return date.value + mark
  }

  composeRange() {
    const from = this.readDate('from')
    const to = this.readDate('to')
    if (from.state === 'invalid' || to.state === 'invalid') return ''
    if (from.state === 'empty' && to.state === 'empty') return ''

    return `${from.value ?? '..'}/${to.value ?? '..'}`
  }

  composeDecade() {
    const year = this.fieldValue('decade', 'year')
    if (year === '' || year.length > 4) return ''

    const padded = year.padStart(4, '0')
    const century = this.eraTargets.find((radio) => radio.checked)?.value === 'century'
    return century ? `${padded.slice(0, 2)}XX` : `${padded.slice(0, 3)}X`
  }

  /**
   * @param {string} group
   * @returns {{state: 'empty'|'invalid'|'ok', value?: string}}
   */
  readDate(group) {
    const [year, month, day] = ['year', 'month', 'day'].map((part) => this.fieldValue(group, part))
    if (year === '' && month === '' && day === '') return { state: 'empty' }
    if (year === '' || (month === '' && day !== '')) return { state: 'invalid' }
    if (year.length > 4 || month.length > 2 || day.length > 2) return { state: 'invalid' }

    const parts = [year.padStart(4, '0')]
    if (month !== '') parts.push(month.padStart(2, '0'))
    if (day !== '') parts.push(day.padStart(2, '0'))
    return { state: 'ok', value: parts.join('-') }
  }

  fieldValue(group, part) {
    const field = this.fieldTargets.find(
      (input) => input.dataset.group === group && input.dataset.part === part,
    )
    if (!field) throw new Error(`date helper has no ${group} ${part} field`)

    return field.value.trim()
  }

  /** @returns {HTMLInputElement[]} the date fields of the shown mode's panel */
  activeFields() {
    const panel = this.shownPanel()
    return this.fieldTargets.filter((field) => panel.contains(field))
  }

  /** @returns {HTMLElement} the panel of the selected mode */
  shownPanel() {
    const mode = this.currentMode()
    const panel = this.panelTargets.find((candidate) => candidate.dataset.mode === mode)
    if (!panel) throw new Error(`date helper has no panel for mode: ${mode}`)

    return panel
  }
}
